import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { differenceInDays } from "date-fns";
import { useDateFormat } from "@/contexts/DateFormatContext";
import { AlertTriangle, Star, Sparkles } from "lucide-react";
import { BETA_CONFIG } from "@/config/beta";

type Tier = "free" | "pro" | "beta_pro";

interface TierBadgeProps {
  tier: Tier;
  size?: "sm" | "md" | "lg";
  showUpgradeLink?: boolean;
  expiresAt?: Date | null;
  onClick?: () => void;
}

const sizeClasses = {
  sm: "text-[10px] px-2 py-0.5",
  md: "text-xs px-3 py-1",
  lg: "text-sm px-4 py-1.5",
};

export function TierBadge({
  tier,
  size = "md",
  showUpgradeLink = false,
  expiresAt,
  onClick,
}: TierBadgeProps) {
  const { formatDate } = useDateFormat();
  const baseClasses = cn(
    "inline-flex items-center gap-1 rounded-full font-semibold transition-all duration-200",
    sizeClasses[size],
    onClick && "cursor-pointer hover:opacity-90"
  );

  if (BETA_CONFIG.isBetaMode) {
    return (
      <span
        className={cn(
          baseClasses,
          "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sm shadow-primary/20"
        )}
      >
        <Sparkles className="w-3 h-3" />
        {BETA_CONFIG.betaBadgeText}
      </span>
    );
  }

  // Check if beta_pro is expiring soon (within 14 days)
  const isExpiringSoon = expiresAt && differenceInDays(expiresAt, new Date()) <= 14;
  const daysUntilExpiry = expiresAt ? differenceInDays(expiresAt, new Date()) : null;

  const getTooltipContent = () => {
    switch (tier) {
      case "free":
        return "Click to upgrade";
      case "pro":
        if (expiresAt) {
          return `Pro access until ${formatDate(expiresAt, "long")}`;
        }
        return "Manage subscription";
      case "beta_pro":
        if (expiresAt) {
          return `Free Pro access until ${formatDate(expiresAt, "long")}`;
        }
        return "Beta Pro access";
      default:
        return "";
    }
  };

  const renderBadge = () => {
    switch (tier) {
      case "free":
        return (
          <span
            onClick={onClick}
            className={cn(baseClasses, "bg-muted text-muted-foreground")}
          >
            Free
          </span>
        );

      case "pro":
        return (
          <span
            onClick={onClick}
            className={cn(
              baseClasses,
              "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sm shadow-primary/20"
            )}
          >
            <Star className="w-3 h-3 fill-current" />
            Pro
          </span>
        );

      case "beta_pro":
        // Show warning style if expiring soon
        if (isExpiringSoon && daysUntilExpiry !== null) {
          return (
            <span
              onClick={onClick}
              className={cn(
                baseClasses,
                "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
              )}
            >
              <AlertTriangle className="w-3 h-3" />
              Pro expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
            </span>
          );
        }

        return (
          <span
            onClick={onClick}
            className={cn(
              baseClasses,
              "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sm shadow-primary/20"
            )}
          >
            <Star className="w-3 h-3 fill-current" />
            Pro (Beta)
          </span>
        );

      default:
        return null;
    }
  };

  const badge = renderBadge();

  // Always wrap in tooltip for clickable badges
  if (onClick) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {tier === "free" && showUpgradeLink ? (
              <div className="inline-flex items-center gap-2">
                {badge}
                <button
                  onClick={onClick}
                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Upgrade →
                </button>
              </div>
            ) : (
              badge
            )}
          </TooltipTrigger>
          <TooltipContent>
            <p>{getTooltipContent()}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (tier === "free" && showUpgradeLink) {
    return (
      <div className="inline-flex items-center gap-2">
        {badge}
        <button
          onClick={onClick}
          className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
        >
          Upgrade →
        </button>
      </div>
    );
  }

  return badge;
}
